import { styled as muiStyled } from '@mui/material/styles'
import Layout from '@/layouts/index'
import Page from '@/components/Page'
import find from 'lodash/find'
import pageService, {
  GlobalSetting,
  SeoMetadata,
  getAvailablePrompts,
  settingService,
} from '@/services/page'
import pathService from '@/services/path'
import { shinyCssWithoutHoverEffect } from '@/theme/palette'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { APPS } from '@/appConfig'
import type { ParsedUrlQuery } from 'querystring'
import { useTheme, Autocomplete, TextField, Typography, Container, Card, Grid } from '@mui/material'
import {
  PromptInterfaceFragment,
  PromptSupportingLanguageMap,
} from '@/generated/graphql.prompts.node'
import { LoadingButton } from '@mui/lab'
import { useState } from 'react'
import { $enum } from 'ts-enum-util'
import { useVisitorGeneratePromptLazyQuery } from '@/generated/graphql'
import { RootStyle } from '@/sections/landing/Shares'
import { languageTransformer, urlQueryParamsForEntrepreneurPage } from '@/services/prompts/utils'
import { useRouter } from 'next/router'
import Typewriter from '@/components/Typewriter'
import { SHARED_PROMPT_HEADER_FOR_CUSTOM_PROMPT_ENGINEERING } from '@/utils/constants'

interface PathProps extends ParsedUrlQuery {
  appId: string
}

interface IndexProps {
  title?: string
  globalSetting?: GlobalSetting
  seoMetadataItemsCollection?: SeoMetadata[]
  prompts: PromptInterfaceFragment[]
}

Entrepreneur.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout variant="main">{page}</Layout>
}

enum MoneyWishToSpendMap {
  'u_1000' = '1000 USD',
  'u_2000' = '2000 USD',
  'u_3000' = '3000 USD',
  'u_4000' = '4000 USD',
  'u_5000' = '5000 USD',
  'u_6000' = '6000 USD',
  'u_7000' = '7000 USD',
  'u_8000' = '8000 USD',
  'u_9000' = '9000 USD',
}

const StyledCard = muiStyled(Card)`
  ${shinyCssWithoutHoverEffect}
  min-height: 100vh;
`

export default function Entrepreneur({
  title,
  prompts,
  globalSetting,
  seoMetadataItemsCollection,
}: IndexProps) {
  const targetPromptEngineering = find(globalSetting?.promptEngineeringsCollection.items ?? [], {
    purpose: 'entrepreneur',
  })

  const [
    visitorGeneratePromptLazyQuery,
    { loading: visitorGeneratePromptLazyQueryLoading, data: visitorGeneratePromptLazyQueryData },
  ] = useVisitorGeneratePromptLazyQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  })
  const router = useRouter()
  const theme = useTheme()
  const searchString: urlQueryParamsForEntrepreneurPage = {
    language:
      (router.query.language as PromptSupportingLanguageMap | undefined) ??
      PromptSupportingLanguageMap.English,
  }
  const [selectLanguage, setSelectLanguage] = useState<PromptSupportingLanguageMap>(
    searchString.language
  )
  const [moneyWishToSpend, setMoneyWishToSpend] = useState<MoneyWishToSpendMap>(
    MoneyWishToSpendMap.u_1000
  )

  const renderText =
    visitorGeneratePromptLazyQueryData?.generatePrompt.createResponse?.choices[0]?.text

  return (
    <Page title={title ?? ''} metadataItems={seoMetadataItemsCollection}>
      <RootStyle theme={theme} backgroundColor="">
        <Container>
          <StyledCard sx={{ p: 5 }}>
            <Typography sx={{ mb: 3 }} color="primary" variant="h3">
              Entrepreneur Only!
            </Typography>
            <Typewriter
              hideCursor
              styleOverPropsForText={{
                sx: {
                  color: 'common.white',
                },
                variant: 'body1',
              }}
              speed={8}
              input={
                'Tell Us how much money wish to spend and AI will generate a business idea for you and make as much money as you want.'
              }
            />
            <Grid container spacing={4} sx={{ pt: 4, pb: 5 }}>
              <Grid item xs={12}>
                <Autocomplete
                  disablePortal
                  id="language"
                  value={languageTransformer(selectLanguage)}
                  options={$enum(PromptSupportingLanguageMap)
                    .getValues()
                    .map((language) => languageTransformer(language))}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Output Language"
                      helperText={`Currently Supporting ${
                        $enum(PromptSupportingLanguageMap).getValues().length
                      } languages`}
                    />
                  )}
                  onChange={(_event, values) => {
                    setSelectLanguage(
                      values?.id ??
                        (PromptSupportingLanguageMap.English as PromptSupportingLanguageMap)
                    )
                  }}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  disablePortal
                  id="money spend"
                  value={moneyWishToSpend}
                  options={$enum(MoneyWishToSpendMap).getValues()}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Money wish to spend"
                      helperText={`Currently Only Supporting ${
                        $enum(MoneyWishToSpendMap).getValues().length
                      } options`}
                    />
                  )}
                  onChange={(_event, values) => {
                    setMoneyWishToSpend(
                      values ?? (MoneyWishToSpendMap.u_1000 as MoneyWishToSpendMap)
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <LoadingButton
                  variant="outlined"
                  fullWidth
                  size="large"
                  loading={visitorGeneratePromptLazyQueryLoading}
                  onClick={() =>
                    visitorGeneratePromptLazyQuery({
                      variables: {
                        promptId: 'entrepreneur_making_as_much_money_as_possible',
                        language: selectLanguage,
                        prompt: moneyWishToSpend,
                      },
                      fetchPolicy: 'no-cache',
                      context:
                        targetPromptEngineering &&
                        targetPromptEngineering.sys &&
                        targetPromptEngineering.sys.id
                          ? {
                              headers: {
                                [SHARED_PROMPT_HEADER_FOR_CUSTOM_PROMPT_ENGINEERING]:
                                  targetPromptEngineering.sys.id,
                              },
                            }
                          : {},
                    })
                  }
                >
                  Ask!
                </LoadingButton>
              </Grid>
              <Grid item xs={12}>
                {visitorGeneratePromptLazyQueryData && renderText && (
                  <Typewriter input={renderText} />
                )}
              </Grid>
            </Grid>
          </StyledCard>
        </Container>
      </RootStyle>
    </Page>
  )
}

export const getStaticPaths: GetStaticPaths<PathProps> = async () => {
  const { getDefaultPaths } = pathService()
  const paths = APPS.flatMap((app) => getDefaultPaths(app.id))

  return {
    paths,
    fallback: true,
  }
}

export const getStaticProps: GetStaticProps<IndexProps, PathProps> = async ({ params, locale }) => {
  if (!params) throw new Error('No path parameters found')

  const { appId } = params
  const { getPage } = pageService(appId)
  const { getGlobalSetting } = settingService(appId)
  const [{ title, seoMetadataItemsCollection }, globalSetting, data = []] = await Promise.all([
    getPage('/', { locale }),
    getGlobalSetting({ locale }),
    getAvailablePrompts(appId),
  ])

  return {
    props: {
      prompts: data,
      title: title ?? '',
      globalSetting,
      seoMetadataItemsCollection,
    },
    revalidate: 3600,
  }
}
